import { useState, useEffect, useCallback } from "react";
import { cmsApi, type CmsPage, type CmsBlock, type SeoData } from "@/services/pages";

interface BlockField {
  key: string;
  label: string;
  type: "text" | "textarea" | "image" | "toggle";
  placeholder?: string;
}

const BLOCK_TYPES: Record<string, { label: string; icon: string; fields: BlockField[]; items?: { label: string; fields: BlockField[] } }> = {
  hero: {
    label: "Hero",
    icon: "ri-fullscreen-line",
    fields: [
      { key: "kicker", label: "Kicker", type: "text", placeholder: "Small eyebrow label" },
      { key: "heading", label: "Headline", type: "text", placeholder: "Main headline" },
      { key: "subheading", label: "Subheading", type: "textarea", placeholder: "Supporting line" },
      { key: "image", label: "Background image URL", type: "image" },
      { key: "ctaLabel", label: "Button label", type: "text", placeholder: "e.g. Explore projects" },
      { key: "ctaHref", label: "Button link", type: "text", placeholder: "/projects" },
    ],
  },
  heading: {
    label: "Heading + text",
    icon: "ri-heading",
    fields: [
      { key: "kicker", label: "Kicker", type: "text" },
      { key: "title", label: "Title", type: "text" },
      { key: "body", label: "Body", type: "textarea" },
      { key: "align", label: "Align", type: "text", placeholder: "center | left" },
    ],
  },
  text: {
    label: "Text section",
    icon: "ri-text",
    fields: [
      { key: "title", label: "Title", type: "text" },
      { key: "body", label: "Body (HTML allowed)", type: "textarea" },
    ],
  },
  stats: {
    label: "Stats",
    icon: "ri-bar-chart-2-line",
    fields: [{ key: "title", label: "Section title", type: "text" }],
    items: {
      label: "Stat",
      fields: [
        { key: "value", label: "Value", type: "text", placeholder: "e.g. 12%" },
        { key: "label", label: "Label", type: "text", placeholder: "e.g. Avg. returns" },
      ],
    },
  },
  cards: {
    label: "Cards grid",
    icon: "ri-layout-grid-line",
    fields: [
      { key: "kicker", label: "Kicker", type: "text" },
      { key: "title", label: "Section title", type: "text" },
    ],
    items: {
      label: "Card",
      fields: [
        { key: "title", label: "Title", type: "text" },
        { key: "body", label: "Body", type: "textarea" },
        { key: "icon", label: "Icon (remix class)", type: "text", placeholder: "ri-home-4-line" },
        { key: "image", label: "Image URL", type: "image" },
      ],
    },
  },
  image_text: {
    label: "Image + text",
    icon: "ri-image-2-line",
    fields: [
      { key: "kicker", label: "Kicker", type: "text" },
      { key: "title", label: "Title", type: "text" },
      { key: "body", label: "Body (HTML allowed)", type: "textarea" },
      { key: "image", label: "Image URL", type: "image" },
      { key: "reverse", label: "Image on the right", type: "toggle" },
    ],
  },
  faq: {
    label: "FAQ",
    icon: "ri-question-answer-line",
    fields: [{ key: "title", label: "Section title", type: "text" }],
    items: {
      label: "Question",
      fields: [
        { key: "q", label: "Question", type: "text" },
        { key: "a", label: "Answer", type: "textarea" },
      ],
    },
  },
  cta: {
    label: "Call to action",
    icon: "ri-megaphone-line",
    fields: [
      { key: "title", label: "Title", type: "text" },
      { key: "body", label: "Body", type: "textarea" },
      { key: "label", label: "Button label", type: "text" },
      { key: "href", label: "Button link", type: "text" },
      { key: "image", label: "Background image URL", type: "image" },
    ],
  },
  banner: {
    label: "Banner",
    icon: "ri-image-line",
    fields: [
      { key: "title", label: "Title", type: "text" },
      { key: "image", label: "Image URL", type: "image" },
    ],
  },
};

function EmptyHint() {
  return (
    <div className="rounded-xl border border-dashed border-background-300 p-6 text-center text-sm text-foreground-600">
      No sections yet. Add one below to start structuring this page.
    </div>
  );
}

function FieldInput({ field, value, onChange }: { field: BlockField; value: unknown; onChange: (v: unknown) => void }) {
  const base = "w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400";
  if (field.type === "textarea") {
    return <textarea rows={3} className={`${base} resize-none`} placeholder={field.placeholder} value={(value as string) || ""} onChange={(e) => onChange(e.target.value)} />;
  }
  if (field.type === "toggle") {
    return (
      <button type="button" onClick={() => onChange(!value)} className="inline-flex items-center gap-2 text-sm text-foreground-800">
        <span className={`w-9 h-5 rounded-full p-0.5 transition-colors ${value ? "bg-primary-600" : "bg-background-200"}`}>
          <span className={`block w-4 h-4 rounded-full bg-background-50 transition-transform ${value ? "translate-x-4" : ""}`}></span>
        </span>
        {value ? "Yes" : "No"}
      </button>
    );
  }
  return <input className={base} placeholder={field.placeholder} value={(value as string) || ""} onChange={(e) => onChange(e.target.value)} />;
}

function BlockEditor({ block, index, onChange, onRemove }: { block: CmsBlock; index: number; onChange: (b: CmsBlock) => void; onRemove: () => void }) {
  const cfg = BLOCK_TYPES[block.type];
  if (!cfg) return null;
  const set = (key: string, value: unknown) => onChange({ ...block, [key]: value });

  return (
    <div className="rounded-2xl bg-background-100 border border-background-200 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-accent-100 text-accent-800">
            <i className={cfg.icon}></i>
          </span>
          <span className="text-sm font-semibold text-foreground-950">{cfg.label}</span>
          <span className="text-xs text-foreground-600">Section {index + 1}</span>
        </div>
        <button onClick={onRemove} className="flex items-center justify-center w-8 h-8 rounded-lg text-accent-700 hover:bg-accent-100" aria-label="Remove section">
          <i className="ri-delete-bin-line"></i>
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        {cfg.fields.map((f) => (
          <div key={f.key} className={f.type === "textarea" ? "col-span-2" : ""}>
            <label className="block text-xs font-semibold text-foreground-800 mb-1.5">{f.label}</label>
            <FieldInput field={f} value={block[f.key]} onChange={(v) => set(f.key, v)} />
          </div>
        ))}
      </div>

      {cfg.items && (
        <div className="space-y-2">
          <label className="block text-xs font-semibold text-foreground-800">{cfg.items.label}s</label>
          {Array.isArray(block.items) && (block.items as Record<string, unknown>[]).map((item, i) => (
            <div key={i} className="rounded-xl bg-background-50 border border-background-200 p-3 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-foreground-700">{cfg.items!.label} {i + 1}</span>
                <button
                  onClick={() => set("items", (block.items as unknown[]).filter((_, j) => j !== i))}
                  className="flex items-center justify-center w-6 h-6 rounded-md text-accent-700 hover:bg-accent-100"
                  aria-label="Remove item"
                >
                  <i className="ri-close-line text-sm"></i>
                </button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {cfg.items.fields.map((f) => (
                  <div key={f.key} className={f.type === "textarea" ? "col-span-2" : ""}>
                    <label className="block text-[11px] font-semibold text-foreground-700 mb-1">{f.label}</label>
                    <FieldInput field={f} value={item[f.key]} onChange={(v) => set("items", (block.items as Record<string, unknown>[]).map((it, j) => (j === i ? { ...it, [f.key]: v } : it)))} />
                  </div>
                ))}
              </div>
            </div>
          ))}
          <button
            onClick={() => set("items", [...((block.items as unknown[]) || []), {}])}
            className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full border border-background-300 text-foreground-800 px-4 py-2 hover:bg-background-50"
          >
            <i className="ri-add-line"></i> Add {cfg.items.label.toLowerCase()}
          </button>
        </div>
      )}
    </div>
  );
}

export default function AdminPages() {
  const [data, setData] = useState<CmsPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState<CmsPage | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await cmsApi.list();
      setData(res);
    } catch (e: any) {
      setError(e?.message || "Failed to load pages");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function remove(id: number) {
    if (!window.confirm("Delete this page? This cannot be undone.")) return;
    try {
      await cmsApi.remove(id);
      load();
    } catch (e: any) {
      window.alert(e?.message || "Delete failed");
    }
  }

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-foreground-600">Page-wise content management. Edit sections and auto-generate SEO per page.</p>
        <button onClick={() => setCreateOpen(true)} className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-5 py-2.5 hover:bg-primary-700">
          <i className="ri-add-line"></i> New Page
        </button>
      </div>

      {error && <div className="rounded-2xl bg-accent-100 border border-accent-300 p-4 text-sm text-accent-900">{error}</div>}

      <div className="rounded-2xl bg-background-50 border border-background-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-background-100 text-left text-xs uppercase tracking-wider text-foreground-600">
                <th className="px-4 py-3">Page</th>
                <th className="px-4 py-3">Slug</th>
                <th className="px-4 py-3">SEO Title</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-background-100">
              {loading && (<tr><td colSpan={5} className="px-4 py-10 text-center text-foreground-600"><i className="ri-loader-4-line animate-spin"></i> Loading...</td></tr>)}
              {!loading && data.map((p) => (
                <tr key={p.id} className="hover:bg-background-100/60">
                  <td className="px-4 py-3 font-medium text-foreground-950">{p.title}</td>
                  <td className="px-4 py-3 text-foreground-700">/{p.slug}</td>
                  <td className="px-4 py-3 text-foreground-600 max-w-xs truncate">{p.seo?.title || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${p.status === "published" ? "bg-primary-100 text-primary-900" : "bg-background-200 text-foreground-700"}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      <a href={`/${p.slug}`} target="_blank" rel="noreferrer" className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100" aria-label="View"><i className="ri-external-link-line"></i></a>
                      <button onClick={() => setEditing(p)} className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100" aria-label="Edit"><i className="ri-pencil-line"></i></button>
                      <button onClick={() => remove(p.id)} className="flex items-center justify-center w-8 h-8 rounded-lg text-accent-700 hover:bg-accent-100" aria-label="Delete"><i className="ri-delete-bin-line"></i></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && data.length === 0 && (<tr><td colSpan={5} className="px-4 py-12 text-center text-foreground-600">No pages yet.</td></tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {(editing || createOpen) && (
        <PageEditor
          page={editing}
          onClose={() => { setEditing(null); setCreateOpen(false); }}
          onSaved={() => { setEditing(null); setCreateOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function SeoFields({ seo, setSeo }: { seo: Partial<SeoData>; setSeo: (s: Partial<SeoData>) => void }) {
  const base = "w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400";
  return (
    <div className="grid grid-cols-2 gap-4">
      <div className="col-span-2">
        <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Meta title (≤ 60 chars)</label>
        <input className={base} value={seo.title || ""} onChange={(e) => setSeo({ ...seo, title: e.target.value })} />
        <p className="mt-1 text-[11px] text-foreground-600">{((seo.title || "").length)}/60</p>
      </div>
      <div className="col-span-2">
        <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Meta description (120–160 chars)</label>
        <textarea rows={3} className={`${base} resize-none`} value={seo.description || ""} onChange={(e) => setSeo({ ...seo, description: e.target.value })}></textarea>
        <p className="mt-1 text-[11px] text-foreground-600">{(seo.description || "").length}/160</p>
      </div>
      <div>
        <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Keywords (comma-separated)</label>
        <input className={base} value={seo.keywords || ""} onChange={(e) => setSeo({ ...seo, keywords: e.target.value })} />
      </div>
      <div>
        <label className="block text-xs font-semibold text-foreground-800 mb-1.5">OG image URL</label>
        <input className={base} value={seo.og_image || ""} onChange={(e) => setSeo({ ...seo, og_image: e.target.value })} />
      </div>
    </div>
  );
}

function PageEditor({ page, onClose, onSaved }: { page: CmsPage | null; onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({
    slug: page?.slug || "",
    title: page?.title || "",
    subtitle: page?.subtitle || "",
    hero_image: page?.hero_image || "",
    status: page?.status || "draft",
    template: page?.template || "default",
  });
  const [sections, setSections] = useState<CmsBlock[]>(page?.sections || []);
  const [seo, setSeo] = useState<Partial<SeoData>>(page?.seo || {});
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState("");
  const [addType, setAddType] = useState<string>("hero");

  function addBlock() {
    setSections((s) => [...s, { type: addType }]);
  }

  async function autoSeo() {
    setGenerating(true);
    setError("");
    try {
      const generated = await cmsApi.generateSeo({ ...form, hero_image: form.hero_image || seo.og_image, sections });
      setSeo((prev) => ({
        ...prev,
        title: generated.title,
        description: generated.description,
        keywords: generated.keywords,
        og_image: prev.og_image || generated.og_image,
      }));
    } catch (e: any) {
      setError(e?.message || "Could not generate SEO");
    } finally {
      setGenerating(false);
    }
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = { ...form, sections, seo };
      if (page) await cmsApi.update(page.id, payload);
      else await cmsApi.create(payload);
      onSaved();
    } catch (err: any) {
      setError(err?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  }

  const base = "w-full rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-3xl rounded-3xl bg-background-50 border border-background-200 shadow-xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-background-100 sticky top-0 bg-background-50 z-10">
          <h3 className="font-heading text-lg text-foreground-950">{page ? `Edit Page — /${page.slug}` : "New Page"}</h3>
          <button onClick={onClose} className="flex items-center justify-center w-8 h-8 rounded-lg text-foreground-600 hover:bg-background-100" aria-label="Close"><i className="ri-close-line"></i></button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-6">
          {error && <div className="rounded-lg bg-accent-100 border border-accent-300 px-4 py-3 text-sm text-accent-900">{error}</div>}

          {/* Basics */}
          <div>
            <h4 className="text-sm font-semibold text-foreground-950 mb-3">Page basics</h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Slug *</label>
                <input required value={form.slug} onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))} placeholder="projects" className={base} />
              </div>
              <div>
                <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Status</label>
                <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))} className={base}>
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                </select>
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Title *</label>
                <input required value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} className={base} />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Subtitle / intro</label>
                <input value={form.subtitle} onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))} className={base} />
              </div>
              <div className="col-span-2">
                <label className="block text-xs font-semibold text-foreground-800 mb-1.5">Hero image URL</label>
                <input value={form.hero_image} onChange={(e) => setForm((f) => ({ ...f, hero_image: e.target.value }))} className={base} />
              </div>
            </div>
          </div>

          {/* SEO */}
          <div className="rounded-2xl bg-background-100 border border-background-200 p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-foreground-950">SEO</h4>
              <button type="button" onClick={autoSeo} disabled={generating} className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-accent-600 text-background-50 px-4 py-2 hover:bg-accent-700 disabled:opacity-60">
                <i className={generating ? "ri-loader-4-line animate-spin" : "ri-magic-line"}></i>
                {generating ? "Generating…" : "Auto generate SEO"}
              </button>
            </div>
            <SeoFields seo={seo} setSeo={setSeo} />
          </div>

          {/* Sections */}
          <div>
            <h4 className="text-sm font-semibold text-foreground-950 mb-3">Page sections</h4>
            {sections.length === 0 ? <EmptyHint /> : null}
            <div className="space-y-4">
              {sections.map((block, i) => (
                <BlockEditor
                  key={i}
                  block={block}
                  index={i}
                  onChange={(b) => setSections((s) => s.map((x, j) => (j === i ? b : x)))}
                  onRemove={() => setSections((s) => s.filter((_, j) => j !== i))}
                />
              ))}
            </div>
            <div className="mt-4 flex items-center gap-2">
              <select value={addType} onChange={(e) => setAddType(e.target.value)} className="w-56 rounded-lg border border-background-300 bg-background-50 px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-400">
                {Object.entries(BLOCK_TYPES).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </select>
              <button type="button" onClick={addBlock} className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full border border-background-300 text-foreground-800 px-4 py-2.5 hover:bg-background-100">
                <i className="ri-add-line"></i> Add section
              </button>
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full border border-background-300 text-foreground-800 px-5 py-2.5 hover:bg-background-100">Cancel</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 text-sm font-semibold whitespace-nowrap rounded-full bg-primary-600 text-background-50 px-5 py-2.5 hover:bg-primary-700 disabled:opacity-60">
              {saving ? <><i className="ri-loader-4-line animate-spin"></i> Saving...</> : "Save page"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}